<template>
  <div>
    <div class="message">
      <div class="sentiment pull-right" v-if="sentiment">
        <el-tooltip content="Neutral">
          <span v-if="sentiment === 'neutral'">
            <i class="fa fa-minus neutral"></i>
          </span>
        </el-tooltip>
        <el-tooltip content="Negative">
          <span v-if="sentiment === 'negative'">
            <i class="fa fa-arrow-down negative"></i>
          </span>
        </el-tooltip>
        <el-tooltip content="Positive">
          <span v-if="sentiment === 'positive'">
            <i class="fa fa-arrow-up positive"></i>
          </span>
        </el-tooltip>
      </div>
      <div class="header">
        <div class="avatar pull-left">
          <img
            :src="getAvatar"
            class="img-circle"
            :class="{ clickable: getAvatar !== defaultAvatar }"
            @error="avatarError = true"
            @click="getAvatar !== defaultAvatar && showGallery([[getAvatar], 0])"
            v-if="getAvatar"
          />
          <div
            class="img-circle"
            :style="{ 'background-color': channelMeta[channel].color }"
            v-if="!getAvatar"
          >
            <i class="fa" :class="channelMeta[channel].icon"></i>
          </div>
        </div>
        <div class="originator" v-if="displayName.length > 0">
          <span class="profile-display" @click="handleOpenProfile">{{ displayName }}</span>
          <span class="pull-right">
            <small class="text-muted">{{ originator }}</small>
          </span>
        </div>
        <div class="originator" v-if="displayName.length === 0">
          <span class="profile-display" @click="handleOpenProfile">{{ originator }}</span>
        </div>
        <span class="sent-by pull-right" v-if="isOnAir">
          {{ sentOnAirBy }}
          <i class="fa fa-user"></i>
        </span>
        <div @click.stop="changeDatetimePostFormat" class="time">
          {{ displayDatetime }}
          via {{ channelMeta[channel].title }}
          <span
            class="originator-type img-circle"
            :style="{ 'background-color': channelMeta[channel].color }"
          >
            <i class="fa" :class="channelMeta[channel].icon"></i>
          </span>
        </div>
      </div>
      <div class="clearfix"></div>
      <div class="message-content">
        <div v-html="formattedMessage"></div>
        <div v-if="message.length > messageLength && !isExpanded" class="read-more">
          <a @click="isExpanded = true">
            Show more
            <i class="fa fa-angle-down"></i>
          </a>
        </div>
        <div v-if="message.length > messageLength && isExpanded" class="read-more">
          <a @click="isExpanded = false">
            Show less
            <i class="fa fa-angle-up"></i>
          </a>
        </div>
        <div class="audio-container" v-if="audio.length">
          <audio
            v-for="(aud, index) in audio"
            :src="aud"
            controls="controls"
            :preload="mediaPreload"
            :ref="`audio${index}`"
            :key="aud"
            @play="handleMediaStarted()"
            @ended="handleMediaEnded()"
            @pause="handleMediaEnded()"
          ></audio>
        </div>
        <div class="video-container" v-if="video.length">
          <video
            class="video-item"
            v-for="(vid, index) in video"
            :ref="`video${index}`"
            :src="vid"
            controls="controls"
            :preload="mediaPreload"
            :key="vid"
            @play="handleMediaStarted()"
            @ended="handleMediaEnded()"
            @pause="handleMediaEnded()"
          ></video>
        </div>
        <div class="images-container" v-if="images.length">
          <image-grid
            :box-width="'370'"
            :box-height="'370px'"
            :images="images"
            @imageSelected="showGallery"
          ></image-grid>
        </div>
        <div class="message-container" v-if="document.length">
          <a v-for="(doc, index) in document" :href="doc" :ref="`document${index}`" :key="doc">{{
            `document_${doc.slice(-20)}`
          }}</a>
        </div>
        <div class="message-container" v-if="text.length">
          <a v-for="(txt, index) in text" :href="txt" :ref="`text${index}`" :key="txt">{{
            getDocumentName(txt)
          }}</a>
        </div>
      </div>
    </div>
    <div class="message-tools">
      <el-tooltip
        class="item"
        effect="dark"
        content="Send to on air"
        placement="bottom"
        v-if="showTools && !isOnAir"
      >
        <a class="onair" @click="setInput('onair')">
          <i class="fa fa-user"></i>
        </a>
      </el-tooltip>

      <el-tooltip
        v-if="showTools && threadId && threadId !== '' && inCount > 0"
        class="item"
        effect="dark"
        content="Conversation"
        placement="bottom"
      >
        <a class="thread" @click="showThread">
          <i class="fa fa-reply"></i>
        </a>
      </el-tooltip>
      <div class="thread-counts" v-if="showTools && threadId && threadId !== '' && inCount > 0">
        <span class="in">{{ inCount }}</span>
        <span v-if="outCount > 0">
          /
          <span class="out">{{ outCount }}</span>
        </span>
      </div>

      <el-tooltip v-if="showTools" class="item" effect="dark" content="Comment" placement="bottom">
        <a class="comment" @click="setInput('comment')">
          <i class="fa fa-comment"></i>
        </a>
      </el-tooltip>

      <el-tooltip
        v-if="showTools && channelMeta[channel].title === 'Facebook Comment'"
        class="item"
        effect="dark"
        content="Like"
        placement="bottom"
      >
        <a class="like" @click="likePost(id)">
          <i class="fa fa-thumbs-up"></i>
        </a>
      </el-tooltip>

      <el-tooltip
        v-if="showTools && channelMeta[channel].title === 'Facebook Post'"
        class="item"
        effect="dark"
        content="Export comments"
        placement="bottom"
      >
        <a class="export-comments" @click="exportFacebookPostComments(id)">
          <i class="fa fa-download"></i>
        </a>
      </el-tooltip>

      <el-tooltip
        v-if="showTools && hasTranscript"
        class="item"
        effect="dark"
        content="Transcript"
        placement="bottom"
      >
        <a class="show-audio-transcript" @click="showAudioTranscript = !showAudioTranscript">
          <i class="fa fa-file-audio-o"></i>
        </a>
      </el-tooltip>

      <a
        v-if="commentsAndRepliesCount && !showCommentsAndReplies"
        @click="showCommentsAndReplies = true"
      >
        <i class="fa fa-toggle-down"></i>
        <span class="comment-count">{{ commentsAndRepliesCount }}</span>
      </a>

      <a
        v-if="commentsAndRepliesCount && showCommentsAndReplies"
        @click="showCommentsAndReplies = false"
      >
        <i class="fa fa-toggle-up"></i>
        <span class="comment-count">{{ commentsAndRepliesCount }}</span>
      </a>

      <span v-if="isOnAir">
        <el-tooltip
          class="pull-right"
          effect="dark"
          content="Remove from on air"
          placement="bottom"
        >
          <a class="delete" @click="$emit('removeOnAir')">
            <i class="fa fa-times"></i>
          </a>
        </el-tooltip>
      </span>
      <parent-interaction
        v-if="parentInteraction !== null && parentInteraction.ore !== null"
        :collapsible="autoCollapseParentInteraction"
        :interaction="parentInteraction"
      ></parent-interaction>
    </div>
    <div class="onair-input" v-if="showInput == 'onair'">
      <el-select v-model="computedOnAirUser" filterable placeholder="Send on air to">
        <el-option
          v-for="user in onAirUsers"
          :key="user.id"
          :label="user.display_name"
          :value="user.id"
        ></el-option>
      </el-select>
      <div class="input-controls">
        <el-button type="primary" size="small" class="pull-right" @click="sendOnAir"
          >Send</el-button
        >
      </div>
      <div class="clearfix"></div>
    </div>
    <div class="comment-input" v-if="showInput == 'comment'">
      <el-input
        v-model="commentMessage"
        type="textarea"
        :rows="2"
        placeholder="Leave a comment..."
      ></el-input>
      <div class="input-controls">
        <el-button
          type="primary"
          size="small"
          class="pull-right"
          v-model="commentMessage"
          @click="sendComment"
          >Comment</el-button
        >
      </div>
      <div class="clearfix"></div>
    </div>
    <div class="interact" v-if="showCommentsAndReplies && commentsAndRepliesCount">
      <div v-for="commentOrReply in commentsAndReplies" :key="commentOrReply.batch_uuid">
        <div class="comments" v-if="commentOrReply.type === 'comment'">
          <message-comment
            :datetime="commentOrReply.date_created"
            :message="commentOrReply.value"
          ></message-comment>
        </div>
        <div class="replies" v-if="commentOrReply.type === 'reply'">
          <message-reply
            :fullName="commentOrReply.full_name"
            :datetime="commentOrReply.date_created"
            :message="commentOrReply.value"
          ></message-reply>
        </div>
      </div>
    </div>
    <div class="transcript" v-if="showAudioTranscript">
      <audio-transcript :transcript="audioTranscript"></audio-transcript>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import sanitizeHtml from 'sanitize-html';
import { take } from 'pipe-operator';
import moment from 'moment';

import { nl2br, makeHyperlinks, makeHashtags, makeMentions } from '@/helpers/interaction';

import ImageGrid from '@/components/common/ImageGrid';
import MessageComment from '@/components/interaction/MessageComment';
import MessageReply from '@/components/interaction/MessageReply';
import AudioTranscript from '@/components/interaction/AudioTranscript';

import * as types from '@/store/modules/interaction/mutation-types';

import ParentInteraction from './ParentInteraction';

export default {
  name: 'Message',
  components: {
    AudioTranscript,
    ImageGrid,
    MessageComment,
    MessageReply,
    ParentInteraction,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    threadId: {
      type: Number,
    },
    originator: {
      type: String,
      required: true,
    },
    originatorType: {
      type: String,
      required: true,
      default: 'telegram',
    },
    datetime: {
      type: Object,
      required: true,
    },
    channel: {
      type: String,
      required: true,
    },
    columns: {
      type: Number,
      default: 1,
    },
    displayName: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    images: {
      type: Array,
      default: () => [],
    },
    audio: {
      type: Array,
      default: () => [],
    },
    video: {
      type: Array,
      default: () => [],
    },
    document: {
      type: Array,
      default: () => [],
    },
    text: {
      type: Array,
      default: () => [],
    },
    avatar: {
      type: String,
      default: '/static/assets/icons/user/default_profile_normal.png',
    },
    comments: {
      type: Array,
      default: () => [],
    },
    replies: {
      type: Array,
      default: () => [],
    },
    onAirUsers: {
      type: Array,
      default: () => [],
    },
    isOnAir: {
      type: Boolean,
      default: false,
    },
    sentOnAirBy: {
      type: String,
      default: '',
    },
    onAirId: {
      type: Number,
      default: null,
    },
    datetimeIsChanged: {
      type: Boolean,
    },
    parentInteraction: {
      type: Object,
      default: null,
      required: false,
    },
    sentiment: {
      type: String,
      default: null,
    },
    inCount: {
      type: Number,
      default: 0,
    },
    outCount: {
      type: Number,
      default: 0,
    },
    showTools: {
      type: Boolean,
      default: true,
    },
    autoCollapseParentInteraction: {
      type: Boolean,
      default: false,
    },
    shouldPause: {
      type: Boolean,
      default: false,
    },
    mediaPreload: {
      type: String,
      default: 'auto',
    },
    audioTranscript: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    interval: null,
    avatarError: false,
    channelMeta: {
      facebook_comment: {
        icon: 'fa-facebook',
        color: '#4767aa',
        title: 'Facebook Comment',
      },
      facebook_post: {
        icon: 'fa-facebook',
        color: '#4767aa',
        title: 'Facebook Post',
      },
      facebook_message: {
        icon: 'fa-facebook',
        color: '#4767aa',
        title: 'Facebook Message',
      },
      twitter: { icon: 'fa-twitter', color: '#1da1f2', title: 'Twitter' },
      tweet: { icon: 'fa-twitter', color: '#1da1f2', title: 'Twitter' },
      message: { icon: 'fa-mobile', color: '#2993cf', title: 'SMS' },
      sms: { icon: 'fa-mobile', color: '#2993cf', title: 'SMS' },
      whatsapp: { icon: 'fa-whatsapp', color: '#0fa251', title: 'Whatsapp' },
      whatsappweb: { icon: 'fa-whatsapp', color: '#0fa251', title: 'Whatsapp' },
      instagram: { icon: 'fa-instagram', color: '#ed1f65', title: 'Instagram' },
      telegram: { icon: 'fa-telegram', color: '#1da1f2', title: 'Telegram' },
      dms: { icon: 'fa-mobile', color: '#1da1f2', title: 'Dms' },
      mtech: { icon: 'fa-mobile', color: '#1da1f2', title: 'Mtech' },
      infobip: { icon: 'fa-mobile', color: '#1da1f2', title: 'Infobip' },
      email: { icon: 'fa-envelope', color: '#1da1f2', title: 'E-Mail' },
      survey: { icon: 'fa-question', color: '#e47923', title: 'Survey' },
      profile_inject: { icon: 'fa-file', color: '#000000', title: 'Data Import' },
      ussd: { icon: 'fa-signal', color: '#2993cf', title: 'USSD' },
      webform: { icon: 'fa-upload', color: '#676A6C', title: 'Data Import' },
      tiktok: { icon: 'fa-play-circle', color: '#000000', title: 'TikTok' },
    },
    isExpanded: false,
    showInput: null,
    showCommentsAndReplies: false,
    showAudioTranscript: false,
    selectedOnAirUser: null,
    replyMessage: '',
    commentMessage: '',
    height: 0,
    videoListeners: {},
    commentsAndRepliesCount: 0,
    defaultAvatar: '/static/assets/icons/user/default_profile_normal.png',
    firstname: null,
    lastname: null,
    mediaIsPlaying: false,
    relativeTime: null,
  }),
  created() {
    this.lastname = this.$store.state.user.me.last_name;
    this.firstname = this.$store.state.user.me.first_name;
    this.interval = setInterval(() => {
      this.relativeTime = moment(this.datetime).fromNow();
    }, 10000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
    Object.keys(this.videoListeners).forEach(key => {
      this.$refs[key][0].removeEventListener('ondurationchange', this.videoListeners[key]);
    });
  },
  mounted() {
    this.commentsAndRepliesCount = this.commentsAndReplies.length;
    this.updateHeight();

    if (!this.video.length) {
      return;
    }

    Object.keys(this.$refs)
      .filter(refKey => refKey.startsWith('video'))
      .forEach(vidKey => {
        const vidElem = this.$refs[vidKey][0];
        this.videoListeners[vidKey] = vidElem.addEventListener('durationchange', () => {
          this.updateHeight();
          vidElem.removeEventListener('ondurationchange', this.videoListeners[vidKey]);
        });
      });
  },
  computed: {
    ...mapState('user', ['me']),

    computedOnAirUser: {
      get() {
        if (this.selectedOnAirUser) {
          return this.selectedOnAirUser;
        } else if (this.onAirUsers.find(u => u.id === this.me.id)) {
          return this.me.id;
        }
        return null;
      },

      set(onAirUser) {
        this.selectedOnAirUser = onAirUser;
      },
    },

    displayDatetime() {
      if (this.datetimeIsChanged) {
        const relativeTime = this.relativeTime || moment(this.datetime).fromNow();
        // If the relative time displays in the future, change the message to 'now'
        return relativeTime.startsWith('in') ? 'now' : relativeTime;
      }
      return moment(this.datetime).format('LLLL');
    },

    showReplyOption() {
      const doNotReplyTo = ['Instagram'];
      const channelName = this.channelMeta[this.channel].title;
      return !doNotReplyTo.includes(channelName);
    },

    messageLength() {
      return this.columns && this.columns > 1 ? this.columns * 140 : 140;
    },

    getAvatar() {
      if (this.avatarError) {
        return false;
      }
      return this.avatar || false;
    },
    formattedMessage() {
      return take(this.message)
        .pipe(this.truncate)
        .pipe(sanitizeHtml)
        .pipe(nl2br)
        .pipe(makeHyperlinks)
        .pipe(makeHashtags)
        .pipe(makeMentions)
        .result();
    },
    commentsAndReplies() {
      return []
        .concat(this.replies.map(r => Object.assign(r, { type: 'reply' })))
        .concat(this.comments.map(c => Object.assign(c, { type: 'comment' })))
        .sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
    },
    hasTranscript() {
      return (
        Array.isArray(this.audioTranscript) &&
        this.audioTranscript.some(t => t && t.toString().trim().length > 0)
      );
    },
  },
  watch: {
    selectedOnAirUser() {
      this.sendOnAir();
    },

    isExpanded() {
      this.updateHeight();
    },

    shouldPause() {
      Object.keys(this.$refs)
        .filter(refKey => refKey.startsWith('audio') || refKey.startsWith('video'))
        .forEach(mediaKey => {
          const mediaElem = this.$refs[mediaKey][0];
          if (mediaElem) {
            mediaElem.pause();
          }
        });
    },
  },
  methods: {
    changeDatetimePostFormat() {
      this.$emit('changeDatetimePostFormat', !this.datetimeIsChanged);
    },

    getDocumentName(filename) {
      return filename.endsWith('.bat')
        ? `document_${filename.slice(-20, -4)}.txt`
        : `document_${filename.slice(-20)}`;
    },

    sendReply() {
      this.$store.dispatch('interaction/sendReply', {
        interactionId: this.id,
        message: this.replyMessage,
      });
      this.commentsAndRepliesCount = this.commentsAndRepliesCount + 1;
      const newReply = {
        type: 'reply',
        full_name: `${this.firstname} ${this.lastname}`,
        value: this.replyMessage,
        date_created: new Date(),
      };
      this.replies.push(newReply);
      this.commentsAndReplies = []
        .concat(this.replies.map(r => Object.assign(r, { type: 'reply' })))
        .concat(this.comments.map(c => Object.assign(c, { type: 'comment' })))
        .sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
      this.$forceUpdate();
      this.showInput = null;
      this.replyMessage = '';
    },

    sendComment() {
      this.$store.dispatch('interaction/sendComment', {
        interactionId: this.id,
        message: this.commentMessage,
      });
      this.commentsAndRepliesCount = this.commentsAndRepliesCount + 1;
      const newComment = {
        type: 'comment',
        value: this.commentMessage,
        date_created: new Date(),
      };
      this.comments.push(newComment);
      this.commentsAndReplies = []
        .concat(this.replies.map(r => Object.assign(r, { type: 'reply' })))
        .concat(this.comments.map(c => Object.assign(c, { type: 'comment' })))
        .sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
      this.$forceUpdate();
      this.showInput = null;
      this.commentMessage = '';
    },

    sendOnAir() {
      // If selected user is blank, don't do anything further
      const userId = this.computedOnAirUser;
      if (!userId) {
        return;
      }

      this.showInput = null;
      this.selectedOnAirUser = null;
      const user = this.onAirUsers.filter(u => userId === u.id)[0];
      this.$store.dispatch('interaction/sendToOnAir', {
        interactionId: this.id,
        userName: user.first_name,
        userId,
      });
    },

    updateHeight() {
      // Height plus padding
      this.height = this.$el.offsetHeight + 12;
      this.$emit('heightUpdated', [this.id, this.height]);
    },

    showGallery(args) {
      const [images, imageIndex] = args;
      this.$store.commit(`interaction/${types.SET_GALLERY_IMAGES}`, images);
      this.$store.commit(`interaction/${types.SET_GALLERY_INDEX}`, imageIndex);
      this.$store.commit(`interaction/${types.SET_GALLERY_VISIBLE}`, true);
    },

    showThread() {
      this.$store.dispatch('interaction/fetchThreads', {
        threadId: this.threadId,
        channelType: this.channel,
      });
    },

    setInput(inputType) {
      if (this.showInput === inputType) {
        this.showInput = null;
      } else {
        this.showInput = inputType;
      }
    },

    truncate(str) {
      if (str.length > this.messageLength && !this.isExpanded) {
        return `${str.substring(0, this.messageLength)}...`;
      }
      return str;
    },

    switchDateOutput() {
      this.$emit('switchDateOutput');
    },

    exportFacebookPostComments(interactionId) {
      this.$store.dispatch('interaction/exportFacebookPostComments', { interactionId });
    },

    likePost(id) {
      this.$store.dispatch('interaction/likeFacebookPost', { id });
    },

    handleOpenProfile() {
      this.$emit('onOpenProfile', this.originatorType, this.originator);
    },

    handleMediaStarted() {
      this.$emit('mediaStarted');
      this.isPlaying = true;
    },

    handleMediaEnded() {
      this.$emit('mediaEnded');
      this.isPlaying = false;
    },
  },

  destroyed() {
    if (this.isPlaying) {
      this.handleMediaEnded();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';

.originator {
  font-size: 14px;
  font-weight: bold;
  color: $colony-primary;
}

.time {
  font-size: 11px;
  color: #90949c;
  cursor: pointer;
}

.avatar .clickable {
  cursor: pointer;
}

.avatar img,
.avatar div {
  margin-right: 10px;
}

.message {
  .avatar div {
    color: #ffffff;
    text-align: center;
    display: inline-block;

    i {
      margin-top: 4px;
      font-size: 32px;
    }
  }
  .avatar img,
  .avatar div {
    height: 40px;
    width: 40px;
  }
  .originator {
    font-size: 14px;
    font-weight: bold;
    color: darken($colony-primary, 5%);
  }
  .channel-type {
    background-color: darken($colony-primary, 5%);
    color: #ffffff;
    font-size: 10px;
    width: 16px;
    height: 16px;
  }
  .originator-type {
    display: inline-block;
    background-color: #4267b2;
    color: #fff;
    font-size: 8px;
    width: 12px;
    height: 12px;
    text-align: center;
  }
  .time {
    font-size: 11px;
    color: #90949c;
  }
  .message-content {
    margin-top: 10px;

    img {
      border: 1px solid #eee;
      width: 100%;
    }
  }
  .audio-container {
    text-align: center;
  }
  .video-container {
    text-align: center;
  }
  .images-container {
    height: 370px;
  }
  .video-item {
    height: 370px !important;
  }
  .audio-container,
  .images-container,
  .video-container {
    margin-top: 8px;
  }
}

.message-tools {
  width: 100%;
  padding-top: 10px;

  i.fa {
    font-size: 16px;
    font-weight: normal;
    margin-right: 2px;
  }

  i.fa:last-child {
    margin-right: 0;
  }

  a {
    color: #bbb;
    margin-right: 20px;
  }
  a:hover {
    color: $colony-primary;
    background-color: inherit;
  }
}

.interact {
  margin-top: 8px;
  margin-bottom: -13px;
}

.comments,
.replies {
  margin-left: -15px;
  margin-right: -15px;
  padding: 0 15px 15px 15px;
}

.comments {
  background-color: lighten($colony-primary, 42%);
}

.replies {
  background-color: lighten($colony-primary, 42%);
}

.read-more {
  font-size: 0.9em;
  margin: 5px 0;
}

.sent-by {
  color: #90949c;
  font-size: 0.8em;
}

.onair-input,
.reply-input,
.comment-input {
  margin-top: 8px;

  .input-controls {
    margin-top: 3px;
  }
}

.onair-input {
  .el-select {
    width: 100%;
  }
}

.comments-and-replies {
  text-align: right;
  a {
    color: $colony-primary;
  }
}
.sentiment {
  font-size: 0.8em;
  color: #ccc;
}
.profile-display {
  cursor: pointer;
}

.thread-counts {
  display: inline;
  font-size: 0.8em;
  color: #ccc;
  margin-left: -20px;
  margin-right: 20px;

  .in {
    color: #10b981;
  }

  .out {
    color: #3b82f6;
  }
}
</style>

<style lang="scss">
@import '../../assets/style/_variables_live.scss';

.hashtag {
  color: #a6307d;
}
.mention {
  color: darken($colony-primary, 5%);
}
</style>



// WEBPACK FOOTER //
// Message.vue?912db452